import type { RecentEdit } from "@api/generated/model";
import { useTableSettings } from "@app/workspace";
import { textCollator } from "@ui/format";
import type { EditValueText } from "@ui/recentEdits";
import { nextSort, type SortKey, sortKeyOf, sortText } from "@ui/sort";
import type { HTMLAttributes, ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { type CellValue, compileFilter, filterText, moveColumn, orderColumns, type ViewFilter, type ViewKey, viewRows } from "./model";
import { useFilterRow } from "./TableState";

/** One column of a ``DataTable``. A column with a ``value`` sorts and filters by it (unless ``sortable`` says no); one
 *  without (a tick box, the row's buttons) only shows its ``cell``. A cell is drawn again when the columns change (memoise
 *  them with what their cells read) or when its row's attributes (``rowProps``) do. */
export type Column<T> = {
  /** Stable: the person's saved order, sort, filters and hidden columns name the column by it. */
  id: string;
  /** The heading; a function gets the rows shown (a tick box that ticks them all). */
  header: ReactNode | ((rows: readonly T[]) => ReactNode);
  /** The column in plain words (the column list, the sort line, the filter's label); the header when it is text. */
  name?: string;
  /** The header's tooltip. */
  title?: string;
  value?: (row: T) => CellValue;
  /** The cell as text: what it shows without a ``cell``, and what the filter looks for (a formatted date, a label, a
   *  code without thousands grouping). */
  text?: (row: T) => string;
  /** The cell's content when it is more than text; without it the cell shows ``text``, else the value ("—" when empty).
   *  ``view.ownOrder``: the table shows the rows in the page's own order (no sort, no filter). */
  cell?: (row: T, index: number, view: { ownOrder: boolean }) => ReactNode;
  /** Right-aligned digits; the filter takes ``>5``, ``2..8``. */
  numeric?: boolean;
  /** On the header and every cell (``mono``, ``wrap``…); ``numeric`` adds ``n``. */
  className?: string;
  /** A cell's own class (a flagged value). */
  cellClassName?: (row: T) => string | undefined;
  cellProps?: (row: T) => HTMLAttributes<HTMLTableCellElement>;
  /** A heading over consecutive columns of one group (a generation, a repeat). */
  group?: { id: string; header: ReactNode };
  /** The column's cell in the table's footer row. */
  footer?: ReactNode;
  /** A typed cell's latest edit when someone changed it lately (``useRecentEdits``): the cell is coloured (the current
   *  user's or someone else's), and the bar above the table explains it when the cell is focused. */
  edit?: (row: T) => RecentEdit | undefined;
  /** Putting a typed cell's earlier value back; none: it cannot be (read only, or a value the page cannot write). */
  putBack?: (row: T, edit: RecentEdit) => (() => void) | undefined;
  /** A value of the change log the way the column shows it. */
  editText?: EditValueText;
  /** Never moved or hidden (a tick box first, the row's buttons last): it keeps its place. */
  fixed?: boolean;
  sortable?: boolean;
};

export type DataTableModel<T> = {
  /** The columns shown, in the order the person put them. */
  columns: Column<T>[];
  /** The rows to show: filtered and sorted. */
  rows: T[];
  /** All the rows the page gave. */
  total: number;
  sort: SortKey[];
  filters: Record<string, string>;
  /** Columns that may move or be hidden, in the person's order (the hidden ones too). */
  movable: Column<T>[];
  /** The columns the person hid, and their ids. */
  hidden: Column<T>[];
  hiddenIds: ReadonlySet<string>;
  /** The rows are only the first page of more (the sort and filters apply to these). */
  partial: boolean;
  sortBy: (id: string, add: boolean) => void;
  clearSort: () => void;
  setFilter: (id: string, text: string) => void;
  clearFilters: () => void;
  /** Move a movable column to a place among the movable ones (0 = first). */
  move: (id: string, to: number) => void;
  defaultOrder: () => void;
  /** Hide a column (its sort key and filter go with it) or show it again. */
  setHidden: (id: string, hide: boolean) => void;
  showAll: () => void;
  /** The order differs from the page's own. */
  reordered: boolean;
  /** How many filters are in force. */
  filtered: number;
  /** The rows are in the page's own order: no sort and no filter in force. */
  ownOrder: boolean;
  /** The boxes under the headings are shown (opened, or a saved filter is in force). */
  filterRow: boolean;
  showFilterRow: (show: boolean) => void;
  /** Column widths the person dragged (px), by column id; the others take what their cells need. */
  widths: Readonly<Record<string, number>>;
  /** A column's width, or its own again (null). */
  setWidth: (id: string, px: number | null) => void;
  /** Any of the person's settings differs from the page's own (order, hidden, sort, filters, widths). */
  customized: boolean;
  /** Every setting back to the page's own, the filter boxes closed. */
  resetAll: () => void;
};

/** The narrowest and widest a person can drag a column (TableSettings.widths). */
export const COLUMN_WIDTH = { min: 40, max: 2000 } as const;
export const clampWidth = (px: number) => Math.min(Math.max(px, COLUMN_WIDTH.min), COLUMN_WIDTH.max);

export const columnName = <T>(c: Column<T>): string => c.name ?? (typeof c.header === "string" ? c.header : c.id);
export const isSortable = <T>(c: Column<T>) => !!c.value && c.sortable !== false;
export const isFilterable = <T>(c: Column<T>) => !!c.value;

/** A table that sorts (a click on a column heading; Shift+click adds a further key), filters (a box under each heading)
 *  and lets the person move and hide its columns, all remembered with their settings for the next visit on any
 *  computer. ``id`` names the table in those settings (``area.table``, lower case). ``columns`` should be memoised.
 *  ``partial``: the rows are only those loaded so far. ``versions``: columns whose values change without new rows or
 *  columns (a pick's place): a sort or filter on one is applied again when its version changes. */
export function useDataTable<T>(
  id: string,
  columns: Column<T>[],
  rows: readonly T[] | undefined,
  opts?: { partial?: boolean; versions?: Record<string, unknown> },
): DataTableModel<T> {
  const [settings, save] = useTableSettings(id);
  const byId = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);
  const fixed = useMemo(() => new Set(columns.filter((c) => c.fixed).map((c) => c.id)), [columns]);
  const ordered = useMemo(
    () =>
      orderColumns(
        columns.map((c) => c.id),
        settings.order,
        fixed,
      ).flatMap((cid) => byId.get(cid) ?? []),
    [columns, settings.order, fixed, byId],
  );
  const movable = useMemo(() => ordered.filter((c) => !c.fixed), [ordered]);
  const reordered = useMemo(() => {
    const own = columns.filter((c) => !c.fixed);
    return movable.some((c, i) => c.id !== own[i]?.id);
  }, [movable, columns]);
  // a hidden column the page no longer has is forgotten; one column always stays
  const hiddenIds = useMemo(() => {
    const ids = new Set(settings.hidden.filter((cid) => byId.has(cid) && !fixed.has(cid)));
    return movable.some((c) => !ids.has(c.id)) ? ids : new Set<string>();
  }, [settings.hidden, byId, fixed, movable]);
  const shown = useMemo(() => ordered.filter((c) => !hiddenIds.has(c.id)), [ordered, hiddenIds]);
  const hidden = useMemo(() => ordered.filter((c) => hiddenIds.has(c.id)), [ordered, hiddenIds]);
  // keys and filters of columns the page no longer has (or hid) are kept, the column may come back, but not applied
  const usable = useCallback(
    (cid: string) => {
      const c = byId.get(cid);
      return c?.value && !hiddenIds.has(cid) ? c : undefined;
    },
    [byId, hiddenIds],
  );
  const sort = useMemo(
    () =>
      settings.sort.map(sortKeyOf).filter((k) => {
        const c = usable(k.field);
        return !!c && isSortable(c);
      }),
    [settings.sort, usable],
  );
  const filters = settings.filters;
  // the version of a changing column the sort or a filter uses (none in use: a new version sorts nothing again)
  const versions = opts?.versions;
  const _live = versions && [...sort.map((k) => k.field), ...Object.keys(filters)].flatMap((f) => (f in versions ? [versions[f]] : []))[0];
  const view = useMemo(() => {
    const keys: ViewKey<T>[] = sort.flatMap((k) => {
      const value = usable(k.field)?.value;
      return value ? [{ value, descending: k.descending }] : [];
    });
    const tests: ViewFilter<T>[] = Object.entries(filters).flatMap(([cid, text]) => {
      const c = usable(cid);
      const value = c?.value;
      const test = value && compileFilter(text, !!c.numeric);
      return value && test ? [{ value, shown: c.text ?? ((row: T) => filterText(value(row))), test }] : [];
    });
    return { rows: viewRows(rows ?? [], keys, tests, textCollator()), filtered: tests.length };
    // ``live``: a changing column's values are read through the column (its version says when to sort again)
  }, [rows, sort, filters, usable]);
  const [filterRow, showFilterRow] = useFilterRow(view.filtered);

  const update = (patch: Partial<typeof settings>) => save({ ...settings, ...patch });
  return {
    columns: shown,
    rows: view.rows,
    total: rows?.length ?? 0,
    sort,
    filters,
    movable,
    hidden,
    hiddenIds,
    partial: !!opts?.partial,
    sortBy: (cid, add) => update({ sort: nextSort(sort, cid, add).map(sortText) }),
    clearSort: () => update({ sort: [] }),
    setFilter: (cid, text) => {
      const { [cid]: _, ...rest } = filters;
      update({ filters: text.trim() ? { ...rest, [cid]: text } : rest });
    },
    clearFilters: () => update({ filters: {} }),
    move: (cid, to) =>
      update({
        order: moveColumn(
          movable.map((c) => c.id),
          cid,
          to,
        ),
      }),
    defaultOrder: () => update({ order: [] }),
    setHidden: (cid, hide) => {
      const { [cid]: _, ...otherFilters } = filters;
      update(
        hide
          ? { hidden: [...hiddenIds, cid], sort: settings.sort.filter((k) => sortKeyOf(k).field !== cid), filters: otherFilters }
          : { hidden: [...hiddenIds].filter((x) => x !== cid) },
      );
    },
    showAll: () => update({ hidden: [] }),
    reordered,
    filtered: view.filtered,
    ownOrder: sort.length === 0 && view.filtered === 0,
    filterRow,
    showFilterRow,
    widths: settings.widths,
    setWidth: (cid, px) => {
      const { [cid]: _, ...rest } = settings.widths;
      update({ widths: px == null ? rest : { ...rest, [cid]: Math.round(clampWidth(px)) } });
    },
    customized:
      reordered ||
      hiddenIds.size > 0 ||
      settings.sort.length > 0 ||
      Object.keys(filters).length > 0 ||
      Object.keys(settings.widths).length > 0,
    resetAll: () => {
      update({ order: [], hidden: [], sort: [], filters: {}, widths: {} });
      showFilterRow(false);
    },
  };
}
